import { useQuery } from '@apollo/client';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { OrderListType } from 'models/order';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../loader/loader.component';
import { getChildClinicsWithParentTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';

type ClinicOrderFilterProps = {
    clinicId: string | null;
    orderListType: OrderListType;
    onClinicChange: (orderListType: OrderListType, clinicId: string | null) => void;
    isDentistUser: boolean;
};

export const ClinicOrderFilter: FC<ClinicOrderFilterProps> = ({
    clinicId,
    orderListType,
    onClinicChange,
    isDentistUser,
}: ClinicOrderFilterProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const hideOtherClinicsOrdersForDentistUser = customerSettings.getCustomerSetting(
        currentOrg?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.HIDE_OTHER_CLINICS_ORDERS_FOR_DENTIST_USER,
    );

    const { data: { childClinicsWithParent } = { childClinicsWithParent: [] }, loading } = useQuery<{
        childClinicsWithParent: Organization[];
    }>(getChildClinicsWithParentTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });
    if (loading) {
        return <Loader />;
    }
    const handleClinicChange = (event: SelectChangeEvent) => {
        const clinicSelectValue = event.target.value;
        if (clinicSelectValue === OrderListType.MY_ORDERS || clinicSelectValue === OrderListType.ALL_ORDERS) {
            onClinicChange(clinicSelectValue, null);
            return;
        }
        onClinicChange(OrderListType.ON_CLINIC, clinicSelectValue);
    };
    return (
        <FormControl fullWidth>
            <InputLabel id="clinic-select-label">{translate('orders:ORDERS')}</InputLabel>
            <Select
                value={orderListType || clinicId}
                onChange={handleClinicChange}
                labelId="clinic-select-label"
                label={translate('orders:ORDERS')}
            >
                {hideOtherClinicsOrdersForDentistUser && isDentistUser ? (
                    <MenuItem value={OrderListType.MY_ORDERS.toString()}>{translate('orders:MY_ORDERS')}</MenuItem>
                ) : (
                    [
                        <MenuItem key="my-orders" value={OrderListType.MY_ORDERS.toString()}>
                            {translate('orders:MY_ORDERS')}
                        </MenuItem>,
                        <MenuItem key="all-orders" value={OrderListType.ALL_ORDERS.toString()}>
                            {translate('orders:ALL_ORDERS')}
                        </MenuItem>,
                        ...childClinicsWithParent.map((clinic) => (
                            <MenuItem key={clinic._id} value={clinic._id}>
                                {clinic.name}
                            </MenuItem>
                        )),
                    ]
                )}
            </Select>
        </FormControl>
    );
};
